import {createSlice} from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment/moment';
import API from '../../app/api';
import config from '../../app/config';
import {getConfig} from '../../util/configUtil';
import {setSnackbar} from './globals';
import {formatNoteType} from '../../components/notes/notesUtil';

export const notesSlice = createSlice({
    name: 'notes',
    initialState: {
        loading: undefined,
        patientsNotes: [],
        timelineNotes: [],
        patientId: null,
        filters: {
            user: [],
            spec: [],
            type: [],
            recType: [],
            status: 'all',
            filterNoteType: 'all',
            display: 'show',
            showFirst: 'all',
            showMyNotes: false,
            expanded: false,
            activitySelectedNotes: null,
        },
        noteType: 'clinical',
    },
    reducers: {
        setPatientNotes: (state, notes) => {
            if (notes.payload.notes) {
                state.patientsNotes = _.reverse(_.sortBy(
                    notes.payload.notes,
                    note => notes.payload.showAllAttendance ? (note.admissionStartDate || note.createdDate): note.lastModifiedDate
                ));
                state.patientId = notes.payload.patient;
            } else {
                state.patientsNotes = null;
                state.patientId = null;
            }
        },
        setLoading: (state, wardsId) => {
            state.loading = wardsId.payload;
        },
        setTimelineNotes: (state, action) => {
            state.timelineNotes = action.payload;
        },
        removeNotes: (state) => {
            state.patientsNotes = null;
            state.patientId = null;
        },
        setNoteFilters: (state, filter) => {
            state.filters = {
                ...state.filters,
                ...filter.payload,
            };
        },
        setNoteType: (state, type) => {
            state.noteType = type.payload;
        },
    },
});

export const loading = (state) => state.notes.loading;
export const patientsNotes = (patientId) => (state) => (state.notes.patientsNotes && patientId === state.notes.patientId) ? state.notes.patientsNotes : null;
export const noteFilters = (state) => state.notes.filters;
export const timelineNotes = (state) => state.notes.timelineNotes;
export const noteType = (state) => state.notes.noteType;

export const {
    setPatientNotes,
    setTimelineNotes,
    setLoading,
    removeNotes,
    setNoteFilters,
    setNoteType,
} = notesSlice.actions;

/**
 * Loads clinical notes for a specific patient based on their id.
 *
 * Depending on the configuration and the `forceCurrentAttendance` flag, this method can either:
 * - Fetch and group all clinical notes from all attendances for the patient, or
 * - Fetch only the clinical notes for the current attendance.
 *
 * @param {Object} patient - The patient whose clinical notes are to be retrieved.
 * @param {AbortSignal} signal - Signal to abort the API request if needed.
 * @param {boolean} [forceCurrentAttendance=false] - If true, fetches clinical notes only for the current attendance.
 * @param {boolean} [timeline=false] - If true, sets the timeline notes instead of patient notes.
 *
 * @example
 * // Load clinical notes for a patient, only for their current attendance.
 * dispatch(loadNotesForPatient('D9010020', '66c79a7abdb9792a3d08d8ed', abortSignal, true));
 */
export const loadNotesForPatient = (patient, signal = null, forceCurrentAttendance = false, timeline = false) => (dispatch, getState) => {
    const patientId = patient?.patient?.__id;
    const patientIdWardId = patient?.__id;
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);
    if (!timeline) dispatch(removeNotes());
    if (showAllAttendance && !forceCurrentAttendance) {
        if (!timeline) dispatch(setLoading(patientId));
        API.getPatientAttendancesHistory(patientId)
            .then(hist => {
                API.getAllClinicalNotesForPatient(patientId, signal)
                    .then((res) => {
                        const notesGroupedByRefId = _.groupBy(res, n => n.patientsInWardsRef);
                        const attClinicalNotes = [];
                        const sortedAdmissions = _.orderBy(hist, ['admission.startDate'], ['asc']);
                        let notesWithoutActivity = notesGroupedByRefId[''] || [];
                        if (!_.isEmpty(notesWithoutActivity))
                            notesWithoutActivity = _.orderBy(notesWithoutActivity, ['createdDate'], ['desc'])

                        const reviewNotes = [];
                        _.forEach(sortedAdmissions, piw => {
                            for (const [key, value] of Object.entries(notesGroupedByRefId)) {
                                const piwKey = _.split(key, ',');
                                if (!_.isEmpty(piwKey) && piwKey[0] === piw?.__id) {
                                    attClinicalNotes.push({
                                        admission: piw?.admission,
                                        admissionStartDate: moment(piw?.admission?.startDate),
                                        admissionEndDate: piw?.admission?.endDate ? moment(piw?.admission?.endDate) : null,
                                        note: _.orderBy(value, ['createdDate'], ['desc']),
                                    });
                                }
                            }

                            if (_.findIndex(attClinicalNotes, att => att.admission ===  piw?.admission) === -1) {
                                attClinicalNotes.push({
                                    admission: piw?.admission,
                                    admissionStartDate: moment(piw?.admission?.startDate),
                                    admissionEndDate: piw?.admission?.endDate ? moment(piw?.admission?.endDate) : null,
                                    note: [],
                                });
                            }
                        });

                        // Filtering of notes that have been created in the patient's context
                        _.forEach(attClinicalNotes, (attNote, index) => {
                            const haveEndDate = attNote.admissionEndDate !== null;
                            const filteredNotes = notesWithoutActivity.filter(note => {
                                const createdDate = moment(note.createdDate);

                                if (haveEndDate) {
                                    const nextAtt = attClinicalNotes[index + 1];
                                    const createdWithinCurrentAdmission = attNote.admissionStartDate.isSameOrBefore(createdDate)
                                        && attNote.admissionEndDate.isSameOrAfter(createdDate);

                                    if (nextAtt) {
                                        return createdWithinCurrentAdmission;
                                    } else {
                                        return createdWithinCurrentAdmission || createdDate.isSameOrAfter(attNote.admissionEndDate);
                                    }
                                } else {
                                    const nextAtt = attClinicalNotes[index + 1];
                                    let endDate = nextAtt?.admissionEndDate || attNote.admissionStartDate;
                                    if (index + 1 === attClinicalNotes.length) {
                                        endDate = moment();
                                    }
                                    return attNote.admissionStartDate.isSameOrBefore(createdDate)
                                        && endDate.isSameOrAfter(createdDate);
                                }
                            });

                            if (!_.isEmpty(filteredNotes)) {
                                reviewNotes.push({
                                    notes: filteredNotes,
                                    indexInsert: index + 1,
                                });
                            }
                        });

                        if (_.isEmpty(attClinicalNotes) && _.isEmpty(reviewNotes) && !_.isEmpty(notesWithoutActivity)) {
                            reviewNotes.push({
                                notes: notesWithoutActivity,
                                indexInsert: 1,
                            });
                        }

                        // Putting notes in the right place in the list
                        _.forEach(reviewNotes, note => {
                            attClinicalNotes.splice(note.indexInsert, 0, {
                                note: note.notes,
                                admission: null,
                                admissionStartDate: note?.notes?.[0]?.createdDate ? moment(note?.notes?.[0]?.createdDate) : null,
                            });
                        });
                        if (timeline) {
                            dispatch(setTimelineNotes(attClinicalNotes));
                        } else {
                            dispatch(setPatientNotes({patient: patientId, notes: attClinicalNotes, showAllAttendance}));
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        dispatch(setSnackbar({type: 'warning', message: 'Unable to retrieve clinical notes.'}));
                        dispatch(setPatientNotes({patient: patientId, notes: []}));
                        dispatch(setTimelineNotes([]));
                    })
                    .finally(() => {
                        dispatch(setLoading(undefined));
                    });
            })
            .catch(error => {
                console.error(error);
                dispatch(setSnackbar({
                    type: 'warning',
                    message: 'Unable to retrieve history visits for clinical notes.',
                }));
                dispatch(setLoading(undefined));
                dispatch(setPatientNotes({patient: patientId, notes: []}));
            });
    } else {
        if (!timeline) dispatch(setLoading(patientId));
        API.getClinicalNotesForPatientByIds(patient, patientIdWardId, signal)
            .then((res) => {
                if (timeline) {
                    dispatch(setTimelineNotes(res));
                } else {
                    dispatch(setPatientNotes({patient: patientId, notes: res}));
                }
            })
            .catch(error => {
                console.error(error);
                dispatch(setSnackbar({type: 'warning', message: 'Unable to retrieve clinical notes.'}));
                dispatch(setPatientNotes({patient: patientId, notes: []}));
                dispatch(setTimelineNotes([]));
            })
            .finally(() => {
                dispatch(setLoading(undefined));
            });
    }
};

export const addNoteForPatient = (patientId, note, patientInWardId, noInfo , forceCurrentAttendance) => (dispatch, getState) => {
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);

    API.addClinicalNote(patientId, note)
        .then(async (noteToAdd) => {
            if (showAllAttendance) {
                if (patientInWardId) {
                    const patient = await API.findPatientInWardById(patientInWardId);
                    dispatch(loadNotesForPatient(patient?.data, null, null, forceCurrentAttendance));
                }
            } else {
                const notesClone = getState().notes.patientsNotes
                    ? _.cloneDeep(getState().notes.patientsNotes)
                    : [];
                notesClone.push(noteToAdd);
                dispatch(setPatientNotes({patient: patientId, notes: notesClone}));
            }

            if (!noInfo) {
                dispatch(setSnackbar({
                    type: 'success',
                    message: 'The note has been added.',
                }));
            }
        })
        .catch(e => {
            console.error(e);
            dispatch(setSnackbar({
                type: 'error',
                message: 'Unknown problem occurred when saving the clinical note.',
            }));
        });
};

export const addNotesForPatient = (patientInWardId, patientId, episodeId, notes, afterSaveEvent) => async (dispatch, getState) => {
    let addedNotes = [];
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);

    for (const note of notes) {
        if (note.referenceId) {
            addedNotes.push(await API.addClinicalNote(patientId, {
                note: note.text,
                patientsInWardsRef: [patientInWardId],
                noteType: note.type,
                referenceId: note.referenceId,
                episodeId: episodeId || null,
            }));
        } else {
            addedNotes.push(await API.addClinicalNote(patientId, {
                note: note.text,
                patientsInWardsRef: [patientInWardId],
                noteType: note.type,
                recordType: note.recordType,
                episodeId: episodeId || null,
            }));
        }
    }

    if (showAllAttendance) {
        const patient = await API.findPatientInWardById(patientInWardId);
        dispatch(loadNotesForPatient(patient?.data));
    } else {
        let notesClone =
            getState().notes.patientsNotes
                ? _.cloneDeep(getState().notes.patientsNotes)
                : [];
        notesClone = notesClone.concat(addedNotes);
        dispatch(setPatientNotes({patient: patientId, notes: notesClone}));
    }
    if (afterSaveEvent) {
        afterSaveEvent(addedNotes.length !== 0);
    }
};

export const hideNoteForPatient = (patientId, note, afterSaveEvent) => (dispatch, getState) => {
    API.hideClinicalNote(patientId, note).then((noteHidden) => {
        if (afterSaveEvent) {
            afterSaveEvent();
        }
    });
};

export const updateNoteForPatient = (patientInWardId, patientId, noteId, note, forceCurrentAttendance) => (dispatch, getState) => {
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);

    API.updateClinicalNote(patientId, note).then(async (noteUpdated) => {
        if (showAllAttendance) {
            const patient = await API.findPatientInWardById(patientInWardId);
            dispatch(loadNotesForPatient(patient?.data, null, null, forceCurrentAttendance));
        } else {
            const notesClone =
                getState().notes.patientsNotes
                    ? _.cloneDeep(getState().notes.patientsNotes)
                    : [];
            for (let n of notesClone) {
                if (noteUpdated.__id === n.__id) {
                    _.merge(n, noteUpdated);
                }
            }
            dispatch(setPatientNotes({patient: patientId, notes: notesClone}));
        }
    });
};

export const commentNoteForPatient = (patientId, noteId, comment) => (dispatch, getState) => {
    API.addClinicalNoteComment(patientId, noteId, comment).then((note) => {
    });
};

export const getOptionsFromClinicalNotes = (notes, showAllNotes) => (dispatch, getState) => {
    const users = new Set();
    const spec = new Set();
    const recType = new Set();
    const noteType = new Map();
    const filter = getState().notes.filters;

    if (showAllNotes) {
        _.forEach(notes, admission => {
            _.forEach(admission.note, note => {
                if (note.createdByName) {
                    if ((_.isEmpty(filter.type) && _.isEmpty(filter.spec))
                        || (!_.isEmpty(filter.type) && filter.type.includes(formatNoteType(note.noteType)))
                        || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                        users.add(note.createdByName);
                    }
                }

                if (note.createdBySpecialty) {
                    if ((_.isEmpty(filter.type) && _.isEmpty(filter.user))
                        || (!_.isEmpty(filter.type) && filter.type.includes(formatNoteType(note.noteType)))
                        || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))) {
                        spec.add(note.createdBySpecialty);
                    }
                }

                if (note.noteType) {
                    if ((_.isEmpty(filter.user) && _.isEmpty(filter.spec))
                        || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))
                        || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                        noteType.set(formatNoteType(note.noteType), note.noteType);
                    }
                }

                if (note.recordType) {
                    if ((_.isEmpty(filter.user) && _.isEmpty(filter.spec))
                        || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))
                        || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                        recType.add(dispatch(recordTypeName(note.recordType)));
                    }
                }
            });
        });
    } else {
        _.forEach(notes, note => {
            if (note.createdByName) {
                if ((_.isEmpty(filter.type) && _.isEmpty(filter.spec))
                    || (!_.isEmpty(filter.type) && filter.type.includes(formatNoteType(note.noteType)))
                    || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                    users.add(note.createdByName);
                }
            }

            if (note.createdBySpecialty) {
                if ((_.isEmpty(filter.type) && _.isEmpty(filter.user))
                    || (!_.isEmpty(filter.type) && filter.type.includes(formatNoteType(note.noteType)))
                    || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))) {
                    spec.add(note.createdBySpecialty);
                }
            }

            if (note.noteType) {
                if ((_.isEmpty(filter.user) && _.isEmpty(filter.spec))
                    || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))
                    || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                    noteType.set(formatNoteType(note.noteType), note.noteType);
                }
            }

            if (note.recordType) {
                if ((_.isEmpty(filter.user) && _.isEmpty(filter.spec))
                    || (!_.isEmpty(filter.user) && filter.user.includes(note.createdByName))
                    || (!_.isEmpty(filter.spec) && filter.spec.includes(note.createdBySpecialty))) {
                    recType.add(dispatch(recordTypeName(note.recordType)));
                }
            }
        });
    }
    return {users, spec, recType, noteType};
};

export const getNotesByNoteFilters = (clinicalNotes = [], showAllNote = false) => (dispatch, getState) => {
    const filters = getState().notes.filters;
    if (config.enableClinicalNotesFilters) {
        let clone = _.cloneDeep(clinicalNotes);

        if (filters.activitySelectedNotes) {
            clone = _.filter(clone, a => a.admission?.__id === filters.activitySelectedNotes.__id);
        }

        if (showAllNote) {
            clone.forEach(att => {
                let notes = att.note;
                notes = _filterByStatus(notes, filters.status);

                if (filters.showMyNotes) {
                    const userName = getState().user.userData?.userName;
                    notes = _.filter(notes, note => {
                        return note?.createdBy === userName;
                    });
                }

                if (!_.isEmpty(filters.user)) {
                    notes = _filterByUser(notes, filters.user);
                }

                if (!_.isEmpty(filters.spec)) {
                    notes = _filterBySpec(notes, filters.spec);
                }

                if (!_.isEmpty(filters.recType)) {
                    notes = _filterByRecType(notes, filters.recType, dispatch);
                }

                if (!_.isEmpty(filters.type)) {
                    notes = _filterByType(notes, filters.display, filters.type);
                }

                notes = _filterByNoteType(notes, filters.filterNoteType);

                if (filters.showFirst !== 'all') {
                    notes = _showLastNote(notes);
                }
                att.note = notes;
            });
        } else {
            clone = _filterByStatus(clone, filters.status);

            if (!_.isEmpty(filters.user)) {
                clone = _filterByUser(clone, filters.user);
            }

            if (!_.isEmpty(filters.spec)) {
                clone = _filterBySpec(clone, filters.spec);
            }

            if (!_.isEmpty(filters.recType)) {
                clone = _filterByRecType(clone, filters.recType, dispatch);
            }

            if (!_.isEmpty(filters.type)) {
                clone = _filterByType(clone, filters.display, filters.type);
            }

            clone = _filterByNoteType(clone, filters.filterNoteType);

            if (filters.showFirst !== 'all') {
                clone = _showLastNote(clone);
            }
        }
        return clone;
    } else {
        return clinicalNotes;
    }
};

const _showLastNote = (clone) => {
    let array = [];

    _.forEach(clone, note => {
        const index = _.findIndex(array, {noteType: note?.noteType});
        if (index !== -1) {
            const check = moment(array[index].createdDate).isBefore(note?.createdDate);
            if (check) {
                _.remove(array, {noteType: note?.noteType});
                array.splice(index, 0, {...note});
            }
        } else {
            array.push(note);
        }
    });
    return array;
};

const _filterByStatus = (notes, status) => {
    switch (status) {
        case 'user': {
            return _.filter(notes, note => {
                return note.noteType === null;
            });
        }
        case 'system': {
            return _.filter(notes, note => {
                return note.noteType !== null;
            });
        }
        case 'all':
        default:
            return notes;
    }
};

const _filterByNoteType = (notes, filterNoteType) => {
    const IMG_NOTE_START_TEXT = 'data:image/png;base64';

    switch (filterNoteType) {
        case 'text': {
            return _.filter(notes, note => {
                if (_.isString(note?.note))
                    return !note?.note?.startsWith(IMG_NOTE_START_TEXT);
            });
        }
        case 'maps': {
            return _.filter(notes, note => {
                if (_.isString(note?.note))
                    return note?.note?.startsWith(IMG_NOTE_START_TEXT);
            });
        }
        case 'all':
        default:
            return notes;
    }
};

const _filterByUser = (notes, filter) => {
    return _.filter(notes, note => {
        return filter.includes(note?.createdByName);
    });
};

const _filterBySpec = (notes, filter) => {
    return _.filter(notes, note => {
        return filter.includes(note?.createdBySpecialty);
    });
};

const _filterByRecType = (notes, filter, dispatch) => {
    return _.filter(notes, note => {
        return filter.includes(dispatch(recordTypeName(note?.recordType)));
    });
};

const _filterByType = (notes, display, type) => {
    return _.filter(notes, note => {
        const formattedName = formatNoteType(note?.noteType);
        return display === 'show'
            ? type.includes(formattedName)
            : !type.includes(formattedName);
    });
};

export const recordTypeName = (recType) => (dispatch, getState) => {
    let name = '';
    _.forEach(getState().dicts.clinicalNoteType, (cNote) => {
        if (cNote.__code === recType) {
            name = cNote.__description;
        }
    });
    return name;
};

export const clearNoteFilters = () => (dispatch, getState) => {
    dispatch(setNoteFilters({
        user: [],
        spec: [],
        type: [],
        recType: [],
        status: getState()?.preferences?.agyle?.defaultNoteFilter || 'all',
        filterNoteType: 'all',
        display: 'show',
        showFirst: 'all',
        showMyNotes: false,
        expanded: false,
        activitySelectedNotes: null,
    }));
};

export default notesSlice.reducer;
